import { AnkrRatioAbi } from '../abis/ankr'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Ankr reader — ankrETH, ankrBNB, ankrAVAX, ankrMATIC.
 *
 * Two calls: `totalSupply()` + `ratio()`. Ankr's `ratio()` is the
 * 1e18-scaled *shares-per-underlying* rate (the inverse of the usual
 * convention) and decreases as staking rewards accrue, so:
 *
 *   exchangeRate (underlying-per-share, 1e18) = 1e18 * 1e18 / ratio
 *   totalAssets                               = totalSupply * 1e18 / ratio
 *
 * Verified on-chain: e.g. ankrETH ratio ≈ 0.812e18 → 1 ankrETH ≈ 1.231 ETH.
 */
export const readerAnkrRatio = (entry: LstRegistryEntry): LstReader => ({
  calls: [
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'ratio', params: [] },
  ],
  abis: [TotalSupplyAbi, AnkrRatioAbi],
  parse: ([supply, ratio]) => {
    const totalSupply = toBigInt(supply)
    const r = toBigInt(ratio)
    if (totalSupply === undefined || r === undefined || r === 0n) {
      return undefined
    }
    return {
      totalAssets: (totalSupply * ONE_E18) / r,
      totalSupply,
      exchangeRate: (ONE_E18 * ONE_E18) / r,
    }
  },
})
